import {
  ChangeDetectorRef,
  Component,
  ViewContainerRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import Flow from '@flowjs/flow.js';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { deepEqual } from 'fast-equals';
import {
  BehaviorSubject,
  fromEvent as observableFromEvent,
  merge,
  Observable,
  of as observableOf,
  Subject,
  switchMap,
} from 'rxjs';
import { debounceTime, filter, map, take, takeUntil } from 'rxjs/operators';

import {
  CandidateBulkActions,
  FullCandidateActions,
  FullCandidateDataService,
  FullCandidateSelectors,
} from '@core/candidate-data';
import {
  CandidateProfileResumeComponent,
  CvActionsComponent,
  CvActionsService,
} from '@core/candidate-legacy';
import type { FullCandidate } from '@core/candidate-types';
import { CandidateAnalytics, TrackAddCandidateMethod } from '@core/candidate-util';
import { filterBoolean } from '@core/common-legacy';
import { HttpApiService } from '@core/http-legacy';
import { HttpRequestData } from '@core/http-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { AppStore } from '@core/legacy-app-store-legacy';
import { PlacementActions } from '@core/placements-data';
import type { Placement } from '@core/placements-types';
import { SourcesActions, TagsActions } from '@core/tags-and-sources-data';
import type { DialogWidth } from '@core/user-interface-legacy';
import {
  AcceptedFileTypesService,
  ButtonComponent,
  ButtonLoadingDirective,
  DialogData,
  DialogRef,
  DialogService,
  FileTypesCategoryEnum,
  GroupComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  NotificationsController,
  ProgressBarComponent,
  RtTooltip,
  ScrollableDirective,
  SpinnerComponent,
  TruncateComponent,
} from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';

import type { FlowFile } from './flow-types';
import { FlowAssignBrowseDirective } from '../../directives/flow/flow-assign-browse';
import { FlowAssignDropDirective } from '../../directives/flow/flow-assign-drop';
import type { AddCandidateFinalValue } from '../add-candidate-form/add-candidate-form.component';
import { AddCandidateFormComponent } from '../add-candidate-form/add-candidate-form.component';

/* eslint-disable max-lines */

@Component({
  selector: 'rt-cv-uploader-dialog',
  templateUrl: './cv-uploader-dialog.component.html',
  styleUrls: ['./cv-uploader-dialog.component.less'],
  host: {
    '(window:keydown.arrowUp)': 'onArrowUp()',
    '(window:keydown.arrowDown)': 'onArrowDown()',
  },
  providers: [CvActionsService, CandidateAnalytics],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FlowAssignDropDirective,
    HeadingComponent,
    ButtonComponent,
    FlowAssignBrowseDirective,
    ProgressBarComponent,
    IconComponent,
    ScrollableDirective,
    SpinnerComponent,
    TruncateComponent,
    IconButtonComponent,
    AddCandidateFormComponent,
    CvActionsComponent,
    CandidateProfileResumeComponent,
    GroupComponent,
    ButtonLoadingDirective,
    RtTooltip,
    TranslatePipe,
  ],
})
export class CVUploaderDialogComponent {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly i18n = inject(I18nService);
  private readonly dialog = inject(DialogRef);
  private readonly dialogService = inject(DialogService);
  private readonly appStore = inject(AppStore);
  private readonly notificationsCtrl = inject(NotificationsController);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly httpApi = inject(HttpApiService);
  private readonly httpRequestData = inject(HttpRequestData);
  private readonly candidateAnalytics = inject(CandidateAnalytics);
  private readonly fullCandidateDataService = inject(FullCandidateDataService);
  private readonly fileTypesService = inject(AcceptedFileTypesService);
  protected readonly cdRef = inject(ChangeDetectorRef);

  public flowFiles: FlowFile[] = [];
  public selectedFile: FlowFile | null;
  public nextFile: FlowFile | null;
  public prevFile: FlowFile | null;
  public flow: any;
  public acceptTypes: string = this.fileTypesService.byCategoryAsString[FileTypesCategoryEnum.CV];
  public isDragging = false;
  public currentCandidate$: BehaviorSubject<any> = new BehaviorSubject(null);
  public currentCandidate: any = null;
  public loading = false;
  public submittingCv = false;

  private jobs: number[] | Array<{ id: number; location_ids: number[] }> = [];
  private talentPools: number[] = [];
  private candidateChanged = false;
  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const data = inject<{
      jobs:
        | number[]
        | Array<{
            id: number;
            location_ids: number[];
          }>;
      talentPools?: number[];
    }>(DialogData);

    this.jobs = data.jobs || [];
    this.talentPools = data.talentPools || [];
  }

  public get errorsText(): string {
    const errors = this.flowFiles.filter(item => item.status === 'error');
    if (errors.length === 1) {
      return this.i18n.translate('app.candidates_import.1_error');
    } else if (errors.length > 1) {
      return this.i18n.translate('app.candidates_import.x_errors', { count: errors.length });
    }
    return '';
  }

  public get parsedFilesText(): string {
    const uploadedFiles = this.flowFiles.filter(item => item.status && item.status !== 'error');
    if (uploadedFiles.length) {
      return this.i18n.translate('app.candidates_import.x_files_parsed', {
        count: uploadedFiles.length,
      });
    }
    return '';
  }

  public get uploadedFilesCounter(): number {
    return this.flowFiles.filter(item => item.status && item.status !== 'error').length;
  }

  public get isLoading(): FlowFile {
    return this.flowFiles.find(file => file.isUploading())!;
  }

  public ngOnInit(): void {
    const flowOptions = {
      testChunks: false,
      chunkSize: 100 * 1024 * 1024,
      uploadMethod: 'POST',
      singleFile: false,
      simultaneousUploads: 2,
      target: this.httpRequestData.buildUrl(`candidates/import`),
      query: {
        offers: JSON.stringify(this.jobs),
        ...(this.talentPools?.length ? { talentPools: this.talentPools } : {}),
      },
      headers: {
        ...HttpApiService.defaultHeaders,
        Authorization: `${this.httpApi.authMethod} ${this.httpApi.authToken}`,
      },
    };

    this.flow = new Flow(flowOptions);
    this.flow.on('filesSubmitted', (file: FlowFile[]) => {
      this.handleFilesSubmitted(file);
      this.cdRef.detectChanges();
    });
    this.flow.on('fileSuccess', (file: FlowFile, message: string) =>
      this.onFileSuccess(file, message),
    );
    this.flow.on('fileError', (file: FlowFile, message: string) => this.onFileError(file, message));

    this.subscribeAppStoreEvents();
    this.observeWindowResize();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onArrowUp(): void {
    if (this.prevFile) {
      this.selectFile(this.prevFile);
      this.cdRef.detectChanges();
    }
  }

  public onArrowDown(): void {
    if (this.nextFile) {
      this.selectFile(this.nextFile);
      this.cdRef.detectChanges();
    }
  }

  public onDragLeave(): void {
    this.isDragging = false;
    this.cdRef.detectChanges();
  }

  public onDragOver(): void {
    this.isDragging = true;
    this.cdRef.detectChanges();
  }

  public onDrop(): void {
    this.isDragging = false;
    this.cdRef.detectChanges();
  }

  //  close helpers

  public closeGuard(): Observable<boolean> {
    const count = this.flowFiles.length;

    if (count) {
      return new Observable(observer => {
        this.dialogService
          .error(
            {
              title: this.i18n.translate('app.candidates_import.confirmation.delete.title', {
                count,
              }),
              message: this.i18n.translate('app.candidates_import.confirmation.delete.message', {
                count,
              }),
              agreeLabel: this.i18n.translate(
                'app.candidates_import.confirmation.delete.agree_label',
                { count },
              ),
            },
            this.viewContainerRef,
          )
          .subscribe(confirmed => {
            if (confirmed) {
              this.cancel();
              observer.next(true);
            } else {
              observer.next(false);
            }
            observer.complete();
          });
      });
    } else {
      return observableOf(true);
    }
  }

  public cancelClick(): void {
    this.dialog.close();
  }

  public selectFile(file: FlowFile): void {
    if (file !== this.selectedFile) {
      this.saveChanges({ silence: true });
      this.selectedFile = file;
      this.currentCandidate$.next({
        name: file.candidate.name,
        offers: file.candidate.placements
          .filter((placement: Placement) => placement.offerId)
          .map((placement: Placement) => ({
            id: placement.offerId,
            locationIds: placement.locationIds,
          })),
        talentPools: file.candidate.placements
          .filter((placement: Placement) => placement.talentPoolId)
          .map((placement: Placement) => placement.talentPoolId),
        placements: file.candidate.placements,
        tags: this.getArrayAsValueArray(file.candidate.tags),
        sources: this.getArrayAsValueArray(file.candidate.sources),
        emails:
          file.candidate.emails && file.candidate.emails.length ? file.candidate.emails : [''],
        phones:
          file.candidate.phones && file.candidate.phones.length ? file.candidate.phones : [''],
        socialLinks: file.candidate.socialLinks || [''],
        links: file.candidate.links || [''],
        customFields: file.candidate.customFields,
        coverLetter: file.candidate.coverLetter,
        photo: null,
        photoUrl: file.candidate.photoUrl,
        cv: null,
        attachment: null,
        mailboxThreadId: null,
        id: file.candidate.id,
        hasParsedFields: file.candidate.fields
          ? file.candidate.fields.some((field: any) => field.origin === 'parsed')
          : false,
        hasAvatar: file.candidate.hasAvatar,
        initials: file.candidate.initials,
      });
      this.refreshPrevNext();
    }
    this.cdRef.detectChanges();
  }

  public addAll(): void {
    this.loading = true;
    this.cdRef.detectChanges();

    this.saveChanges({ silence: true }).subscribe(_ => {
      this.loading = false;
      this.cdRef.detectChanges();
      const message = this.i18n.translate('app.candidates_import.x_candidates_added', {
        count: this.flowFiles.length,
      });
      this.candidateAnalytics.trackAddCandidate(
        TrackAddCandidateMethod.RESUME,
        this.flowFiles.length,
      );
      this.flowFiles = [];

      this.appStore.dispatch({ type: 'offer.pipeline.refresh' });
      this.appStore.dispatch({ type: 'search.refresh.success' });
      this.dialog.close();

      this.notificationsCtrl.success(message);
    });
  }

  public deleteCandidate(file: FlowFile): void {
    if (file.candidate) {
      file.isLoading = true;
      this.store.dispatch(
        CandidateBulkActions.delete({
          payload: { correlationId: generateUuid(), params: { ids: [file.candidate.id] } },
        }),
      );
      this.flowFiles = this.flowFiles.filter(flowFile => flowFile !== file);
      if (this.flowFiles && this.flowFiles.length) {
        this.selectFile(this.flowFiles[0]);
      }
    }
  }

  public saveChanges(
    options: { silence: boolean } = { silence: false },
  ): Observable<boolean | FullCandidate> {
    if (this.selectedFile && this.selectedFile.candidate) {
      const candidateId = this.selectedFile.candidate.id;
      const candidateData: Record<string, any> = { id: candidateId };

      const fields = [
        'name',
        'salutation',
        'title',
        'coverLetter',
        'coverLetterFile',
        'customFields',
        'photo',
      ];
      const arrayFields = ['emails', 'phones', 'links', 'socialLinks'];

      Object.keys(this.currentCandidate!).forEach(objectKey => {
        const value = this.currentCandidate![objectKey];
        if (arrayFields.includes(objectKey)) {
          candidateData[objectKey] = (value || []).filter(Boolean);
        } else if (fields.includes(objectKey)) {
          if (objectKey === 'photo' && !value) {
            return;
          }
          candidateData[objectKey] = value;
        }
      });

      this.selectedFile.silence = options.silence;
      return this.fullCandidateDataService.save({
        data: { ...candidateData, id: candidateId },
        reveal: true,
      });
    } else {
      return observableOf(true);
    }
  }

  public cancelAndRemove(file: FlowFile): void {
    file.cancel();
    this.removeFile(file);
    this.cdRef.detectChanges();
  }

  // resume events

  public updateCv(file: File): void {
    this.submittingCv = true;
    this.cdRef.detectChanges();
    this.store.dispatch(
      FullCandidateActions.updateCv({
        payload: {
          id: this.selectedFile!.candidate.id,
          file: file,
        },
      }),
    );
  }

  public updateParseCv({ resume, parse }: { resume: File; parse: boolean }): void {
    this.submittingCv = true;
    this.cdRef.detectChanges();
    this.store.dispatch(
      FullCandidateActions.updateParseCv({
        payload: {
          id: this.selectedFile!.candidate.id,
          file: resume,
          parse,
        },
      }),
    );
  }

  public deleteCv(): void {
    this.submittingCv = true;
    this.cdRef.detectChanges();
    this.store.dispatch(
      FullCandidateActions.deleteCv({ payload: { id: this.selectedFile!.candidate.id } }),
    );
  }

  public deleteParseCv({ deleteParsedData }: { deleteParsedData: boolean }): void {
    this.submittingCv = true;
    this.cdRef.detectChanges();
    this.store.dispatch(
      FullCandidateActions.deleteParsedCv({
        payload: { id: this.selectedFile!.candidate.id, deleteParsedData },
      }),
    );
  }

  public editPreferredLocations(data: { offerId: number; locationIds: number[] }): void {
    this.store.dispatch(
      PlacementActions.changePreferredWorkLocations({
        payload: {
          placementId: this.currentCandidate.placements.find(
            (placement: Placement) => placement.offerId === data.offerId,
          )?.id,
          locationIds: data.locationIds,
        },
      }),
    );
  }

  //  candidate events

  public candidateChange(data: FullCandidate | AddCandidateFinalValue): void {
    const sanitizedNewTags = this.getArrayFromValueArray(data.tags);
    const sanitizedCurrentTags = this.getArrayFromValueArray(this.currentCandidate!.tags);

    const sanitizedNewSources = this.getArrayFromValueArray(data.sources);
    const sanitizedCurrentSources = this.getArrayFromValueArray(this.currentCandidate!.sources);
    const photoRemoved =
      this.currentCandidate.photoUrl !== null && (<AddCandidateFinalValue>data).photoUrl === null;

    if (photoRemoved) {
      this.removeCandidatePhoto(this.currentCandidate.id);
    }
    this.updateCandidateTags(sanitizedNewTags, sanitizedCurrentTags);
    this.updateCandidateSources(sanitizedNewSources, sanitizedCurrentSources);

    const jobs =
      (<AddCandidateFinalValue>data).jobs ||
      (<FullCandidate>data).placements
        .filter(placement => placement.offerId)
        .map(placement => placement.offerId);

    const talentPools =
      (<AddCandidateFinalValue>data).talentPools ||
      (<FullCandidate>data).placements
        .filter(placement => placement.talentPoolId)
        .map(placement => placement.talentPoolId);
    this.updateCandidateOffers(jobs, talentPools, this.selectedFile!.candidate.placements);

    if (!deepEqual(data, this.currentCandidate)) {
      this.currentCandidate$.next({
        ...this.currentCandidate,
        ...data,
        tags: this.getArrayAsValueArray(sanitizedNewTags),
        sources: this.getArrayAsValueArray(sanitizedNewSources),
      });
      this.candidateChanged = true;
      this.cdRef.detectChanges();
    }
  }

  public onOfferRemove(offerId: number): void {
    const placement = this.selectedFile!.candidate.placements.find(
      (placementItem: any) => placementItem.offerId === offerId,
    );
    if (placement) {
      this.store.dispatch(
        PlacementActions.removeOnePessimistic({ payload: { data: placement.id } }),
      );
      this.actions$
        .pipe(
          ofType(PlacementActions.removeOnePessimisticSuccess),
          map(({ payload }) => payload.data),
          filter(deletedPlacement => deletedPlacement.id === placement.id),
          take(1),
          takeUntil(this.ngOnDestroy$),
        )
        .subscribe(deletedPlacement => {
          const file = this.getFileByCandidateId(deletedPlacement.candidateId);
          if (file) {
            file.candidate.placements = file.candidate.placements.filter(
              (placementItem: { id: number; candidateId: number }) =>
                placementItem.id !== deletedPlacement.id,
            );
          }
        });
    }
    this.cdRef.detectChanges();
  }

  public trackByFile(index: number, file: FlowFile): string {
    return file.uniqueIdentifier;
  }

  private onJobAdd(job: { id: number; locationIds: number[] | null }): void {
    const correlationId = generateUuid();
    this.store.dispatch(
      PlacementActions.createOnePessimistic({
        payload: {
          offerId: job.id,
          locationIds: job.locationIds,
          candidateId: this.selectedFile!.candidate.id,
          correlationId,
        },
      }),
    );
    this.actions$
      .pipe(
        ofType(PlacementActions.createOnePessimisticSuccess),
        filter(({ payload }) => payload.correlationId === correlationId),
        take(1),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(({ payload: { data } }) => {
        const file = this.getFileByCandidateId(data.candidateId);
        if (file) {
          file.candidate.placements.push({ id: data.id, offerId: data.offerId });
        }
      });
  }

  private onTalentPoolAdd(talentPoolId: number): void {
    const correlationId = generateUuid();
    this.store.dispatch(
      PlacementActions.createOnePessimistic({
        payload: {
          talentPoolId,
          candidateId: this.selectedFile!.candidate.id,
          correlationId,
        },
      }),
    );
    this.actions$
      .pipe(
        ofType(PlacementActions.createOnePessimisticSuccess),
        filter(({ payload }) => payload.correlationId === correlationId),
        take(1),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(({ payload: { data } }) => {
        const file = this.getFileByCandidateId(data.candidateId);
        if (file) {
          file.candidate.placements.push({ id: data.id, talentPoolId: data.talentPoolId });
        }
      });
  }

  // eslint-disable-next-line max-lines-per-function
  private subscribeAppStoreEvents(): void {
    const updateCvSuccess$ = this.actions$.pipe(
      ofType(
        FullCandidateActions.updateCvSuccess,
        FullCandidateActions.updateParseCvSuccess,
        FullCandidateActions.deleteCvSuccess,
        FullCandidateActions.deleteParsedCvSuccess,
      ),
      switchMap(({ payload }) =>
        this.store
          .select(FullCandidateSelectors.byIdWithReferences(payload.data.id))
          .pipe(filterBoolean, take(1)),
      ),
    );

    const removePhotoSuccess$ = this.actions$.pipe(
      ofType(FullCandidateActions.removePhotoSuccess),
      switchMap(({ payload }) =>
        this.store
          .select(FullCandidateSelectors.byIdWithReferences(payload.data.id))
          .pipe(filterBoolean, take(1)),
      ),
    );

    this.actions$
      .pipe(
        ofType(
          FullCandidateActions.updateCv,
          FullCandidateActions.updateParseCv,
          FullCandidateActions.deleteCv,
          FullCandidateActions.deleteParsedCv,
          FullCandidateActions.createOnePessimistic,
          FullCandidateActions.updateOnePessimistic,
          FullCandidateActions.removePhoto,
        ),
      )
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        this.selectedFile!.isLoading = true;
        this.cdRef.detectChanges();
      });

    merge(
      this.actions$
        .pipe(
          ofType(
            FullCandidateActions.updateCvFailed,
            FullCandidateActions.updateParseCvFailed,
            FullCandidateActions.deleteCvFailed,
            FullCandidateActions.deleteParsedCvFailed,
            FullCandidateActions.removePhotoFailed,
          ),
        )
        .pipe(map(({ payload }) => ({ candidateId: payload.id, error: payload.error }))),
      this.actions$
        .pipe(ofType(FullCandidateActions.updateOnePessimisticFailed))
        .pipe(map(({ payload }) => ({ candidateId: payload.data.id, error: payload.error }))),
    )
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(payload => {
        if (payload.candidateId) {
          const file = this.getFileByCandidateId(payload.candidateId);
          if (file) {
            if (payload.error && !file.silence) {
              const error = payload.error.error;
              if (error && error.error && error.error[0]) {
                this.notificationsCtrl.error(error.error[0]);
              }
            }
            file.isLoading = false;
            this.cdRef.detectChanges();
          }
        }
      });

    merge(
      updateCvSuccess$,
      removePhotoSuccess$,
      this.actions$.pipe(
        ofType(
          FullCandidateActions.createOnePessimisticSuccess,
          FullCandidateActions.updateOnePessimisticSuccess,
        ),
        switchMap(({ payload }) =>
          this.store
            .select(FullCandidateSelectors.byIdWithReferences(payload.data.id))
            .pipe(filterBoolean, take(1)),
        ),
      ),
    )
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(candidate => {
        const file = this.getFileByCandidateId(candidate.id);
        if (file) {
          file.candidate = candidate;
          file.isLoading = false;
          this.updateFileStatus(file);
          if (!file.silence) {
            this.notificationsCtrl.success(this.i18n.translate('app.common.changes_saved'));
          }
        }
        this.candidateChanged = false;
        this.submittingCv = false;
        this.cdRef.detectChanges();
      });

    updateCvSuccess$
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(candidate => this.candidateChange(candidate));

    this.currentCandidate$
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(currentCandidate => {
        this.currentCandidate = currentCandidate;
        this.cdRef.detectChanges();
      });
  }

  private handleFilesSubmitted(flowFiles: FlowFile[]): void {
    if (flowFiles && flowFiles.length) {
      flowFiles.forEach(flowFile => {
        if (this.fileTypesService.isInvalid(flowFile.file.name, this.acceptTypes)) {
          this.flowFiles.unshift(flowFile);
          this.flow.removeFile(flowFile);
          this.onFileError(
            flowFile,
            JSON.stringify({
              error: this.i18n.translate('app.candidates_import.unsupported_file'),
            }),
          );
        } else if (!this.getFileById(flowFile.uniqueIdentifier)) {
          this.flowFiles.unshift(flowFile);
        }
      });

      this.flow.upload();
    }
  }

  private onFileSuccess(file: FlowFile, message: string): void {
    const response = JSON.parse(message);

    if (response && response.candidate) {
      const flowFile = this.getFileById(file.uniqueIdentifier);

      if (!flowFile) {
        return;
      }

      flowFile.candidate = response.candidate;
      this.updateFileStatus(flowFile);
      this.refreshPrevNext();
      this.cdRef.detectChanges();
    } else {
      this.onFileError(file, message);
    }
  }

  private onFileError(file: FlowFile, message: string): void {
    const data = JSON.parse(message);
    file.status = 'error';
    file.validationError = data && data.error;
    file.error = true;
    this.refreshPrevNext();
    this.cdRef.detectChanges();
  }

  private updateFileStatus(flowFile: FlowFile): void {
    if (!flowFile.candidate.emails?.length) {
      flowFile.status = 'uncompleted';
      flowFile.statusWarningMessage = this.i18n.translate(
        'app.candidates_import.uncompleted_message',
      );
    } else {
      flowFile.status = 'completed';
      flowFile.statusWarningMessage = '';
    }
  }

  private cancel(): void {
    const candidateIds = (this.flowFiles || [])
      .filter((item: any) => item.candidate)
      .flatMap((item: any) => item.candidate.id);

    if (candidateIds) {
      this.store.dispatch(
        CandidateBulkActions.delete({
          payload: { correlationId: generateUuid(), params: { ids: candidateIds } },
        }),
      );
    }

    this.flowFiles.forEach(file => {
      if (file && !file.isUploading()) {
        file.cancel();
      }
    });
  }

  private updateCandidateTags(newTags: string[], currentTags: string[]): void {
    const diff = this.getArrayChanges(newTags, currentTags);
    if (diff.deleted.length) {
      diff.deleted.forEach((tag: any) => {
        this.store.dispatch(
          TagsActions.deleteCandidateTags({
            payload: {
              candidateId: this.selectedFile!.candidate.id,
              tagName: tag,
            },
          }),
        );
      });
    }
    if (diff.created.length) {
      this.store.dispatch(
        TagsActions.createTag({
          payload: { candidateId: this.selectedFile!.candidate.id, tagName: diff.created[0] },
        }),
      );
    }
  }

  private updateCandidateSources(newSources: string[], currentSources: string[]): void {
    const diff = this.getArrayChanges(newSources, currentSources);
    if (diff.created.length) {
      this.store.dispatch(
        SourcesActions.createSource({
          payload: {
            candidateId: this.selectedFile!.candidate.id,
            sourceName: diff.created[0],
          },
        }),
      );
    }
    if (diff.deleted.length) {
      diff.deleted.forEach((source: any) => {
        this.store.dispatch(
          SourcesActions.deleteCandidateSources({
            payload: {
              candidateId: this.selectedFile!.candidate.id,
              sourceName: source,
            },
          }),
        );
      });
    }
  }

  private removeCandidatePhoto(id: number) {
    this.store.dispatch(FullCandidateActions.removePhoto({ payload: { id, reveal: true } }));
  }

  private updateCandidateOffers(
    jobs: Array<{ id: number; locationIds: number[] | null }>,
    talentPools: number[] = [],
    placements: Placement[],
  ): void {
    const jobsMap = jobs.reduce(
      (acc, job) => {
        acc[job.id] = job;
        return acc;
      },
      <Record<number, { id: number; locationIds: number[] | null }>>{},
    );
    const jobsDiff = this.getArrayChanges(
      jobs.map(({ id }) => id),
      placements.filter(({ offerId }) => offerId).map(({ offerId }) => offerId),
    );

    if (jobsDiff.created.length) {
      jobsDiff.created.forEach((offerId: number) => {
        this.onJobAdd(jobsMap[offerId]);
      });
    }

    const talentPoolsDiff = this.getArrayChanges(
      talentPools.map(id => id),
      placements.filter(({ talentPoolId }) => talentPoolId).map(({ talentPoolId }) => talentPoolId),
    );

    if (talentPoolsDiff.created.length) {
      talentPoolsDiff.created.forEach((talentPoolId: number) => {
        this.onTalentPoolAdd(talentPoolId);
      });
    }

    const combinedDeletedOffers = [...jobsDiff.deleted, ...talentPoolsDiff.deleted];
    if (combinedDeletedOffers.length) {
      combinedDeletedOffers.forEach((offerId: number) => {
        this.onOfferRemove(offerId);
      });
    }
  }

  private getArrayChanges<T>(
    newItems: T[],
    currentItems: T[],
  ): {
    deleted: T[];
    created: T[];
  } {
    return {
      deleted: [currentItems, newItems].reduce((a, b) => a.filter((c: any) => !b.includes(c))),
      created: [newItems, currentItems].reduce((a, b) => a.filter((c: any) => !b.includes(c))),
    };
  }

  private refreshPrevNext(): void {
    const uploadedFiles = (this.flowFiles || []).filter(
      item => item.status && item.status !== 'error',
    );

    if (!this.selectedFile) {
      this.nextFile = uploadedFiles[0];
    } else {
      const index = uploadedFiles.indexOf(this.selectedFile!);
      this.prevFile = index > 0 ? uploadedFiles[index - 1] : null;
      this.nextFile = index >= 0 ? uploadedFiles[index + 1] : null;
    }
  }

  private removeFile(file: FlowFile): void {
    this.flowFiles = this.flowFiles.filter(
      _file => _file.uniqueIdentifier !== file.uniqueIdentifier,
    );

    if (!this.flowFiles.length) {
      this.selectedFile = null;
    }
  }

  private getFileById(uniqueIdentifier: string): FlowFile | undefined {
    return this.flowFiles.find(file => file.uniqueIdentifier === uniqueIdentifier);
  }

  private getFileByCandidateId(candidateId: number): FlowFile {
    return this.flowFiles.find(file => file.candidate && file.candidate.id === candidateId)!;
  }

  private getArrayAsValueArray(arr: any[]): any[] {
    return arr && arr.length && !arr[0].value ? arr.map(v => ({ value: v })) : arr;
  }

  private getArrayFromValueArray(arr: any[]): string[] {
    return arr.length && arr[0].value ? arr.map(v => v.value) : arr;
  }

  private observeWindowResize(): void {
    observableFromEvent(window, 'resize')
      .pipe(debounceTime(500), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(event => {
        const target = <Window>event.target;
        this.dialog.setContainerWidth(<DialogWidth>`${target.innerWidth}px`);
      });
  }
}
